import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/api';
import { getAuthToken } from '../utils/auth';

export const useAnalytics = (days = 30) => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState({
    overview: false,
    trends: false,
    albums: false
  });
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const fetchOverview = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(prev => ({ ...prev, overview: true }));
    try {
      const response = await authFetch('/api/artists/analytics/overview');
      if (!response.ok) {
        throw new Error('Failed to fetch overview');
      }
      const data = await response.json();
      setOverview(data);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('Session expirée')) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(err.message);
      console.error('Analytics overview error:', err);
    } finally {
      setLoading(prev => ({ ...prev, overview: false }));
    }
  }, [navigate]);

  const fetchTrends = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(prev => ({ ...prev, trends: true }));
    try {
      const response = await authFetch(`/api/artists/analytics/trends?days=${days}`);
      if (!response.ok) {
        throw new Error('Failed to fetch trends');
      }
      const data = await response.json();
      setTrends(data);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('Session expirée')) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(err.message);
      console.error('Analytics trends error:', err);
    } finally {
      setLoading(prev => ({ ...prev, trends: false }));
    }
  }, [navigate, days]);

  const fetchAlbums = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(prev => ({ ...prev, albums: true }));
    try {
      const response = await authFetch(`/api/artists/analytics/albums?days=${days}`);
      if (!response.ok) {
        throw new Error('Failed to fetch albums performance');
      }
      const data = await response.json();
      setAlbums(data);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('Session expirée')) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(err.message);
      console.error('Analytics albums error:', err);
    } finally {
      setLoading(prev => ({ ...prev, albums: false }));
    }
  }, [navigate, days]);

  const loadAll = useCallback(async () => {
    await Promise.all([
      fetchOverview(),
      fetchTrends(),
      fetchAlbums()
    ]);
  }, [fetchOverview, fetchTrends, fetchAlbums]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refresh = useCallback(() => {
    loadAll();
  }, [loadAll]);

  return {
    overview,
    trends,
    albums,
    loading,
    error,
    refresh
  };
};
